import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { REGIONAL_COVERAGE } from "@/data/regionalCoverage";

/**
 * /gulf-investors — "For Gulf Investors" coverage note.
 *
 * Written in the voice of the Gulf Region Lead: how UAE and GCC limited
 * partners engage with the platform, what they find here, and the basis
 * on which regional coverage operates.
 */
const SVEN = REGIONAL_COVERAGE.find((m) => m.id === "sven-massenhove");

const ARABIC = {
  eyebrow: "ملخّص باللغة العربية",
  title: "للمستثمرين في دول الخليج",
  latinName: "Kellermann Reid & Co.",
  paragraphs: [
    // First paragraph is split so the Latin firm name can be isolated with dir="ltr".
    {
      pre: "كيلرمان ريد وشركاه (",
      post:
        ") دارٌ استثمارية في الأسواق الخاصة، في مرحلة التأسيس الاستراتيجي، تركّز على الائتمان الصناعي المضمون بالأصول، والتمويل الجسري، والاستثمار في فرص الأسهم الصناعية الخاصة، وحلول رأس المال المهيكلة. تتّخذ الشراكة قراراتها الاستثمارية عبر لجنة استثمار موثّقة، ولا تعمل كوكيل توزيع أو وسيط في الأوراق المالية.",
    },
    "يقود التغطية الإقليمية لدولة الإمارات العربية المتحدة ودول مجلس التعاون الخليجي سفين ماسنهوف، مسؤول منطقة الخليج، من مركز دبي المالي العالمي. يتمّ التواصل مع المستثمرين المؤهّلين على أساس التعريف المباشر، وتُدار جميع المناقشات الاستثمارية وإجراءات العناية الواجبة والتوثيق من قِبَل الشراكة مباشرةً.",
    "الشراكة غير مرخّصة ولا خاضعة لرقابة سلطة دبي للخدمات المالية أو هيئة الأوراق المالية والسلع أو أيّ جهة تنظيمية أخرى في دولة الإمارات أو دول الخليج، ولا يُشكّل هذا المحتوى ترويجاً مالياً أو عرضاً لبيع أيّ أوراق مالية. النصّ الكامل لهذه الصفحة متاح باللغة الإنجليزية أدناه.",
  ],
  contact: "للتواصل",
};

const LETTER = [
  "I am based in Dubai, within the DIFC, and I am the partnership's contact point for investors across the United Arab Emirates and the wider GCC. My role is deliberately simple: to understand what a Gulf allocator is trying to achieve, to say plainly whether Kellermann Reid & Co. is a credible fit for it, and — where it is — to open a direct, documented dialogue with the partnership's Capital Formation function.",
  "Gulf capital is sophisticated, patient and increasingly selective about who it backs at platform stage. It has seen enough cycles to distinguish an underwriting culture from a marketing one. What the partnership offers is the former: asset-backed industrial credit, bridge capital and opportunistic industrial equity, written to collateral, contracts and seniority, and decided by an Investment Committee that records its deliberation.",
  "I do not run a sales process. Introductions are made by name, in person where possible, and every conversation follows the same documented pathway as any other institutional relationship the firm holds. If the mandate does not fit, I will say so early — that discipline is the beginning of a long relationship, not the end of a short one.",
];

const WHO = [
  {
    n: "i",
    title: "Sovereign-adjacent & institutional allocators",
    body: "Government-related entities, pension and endowment-style pools and their external managers evaluating anchor or seed positions in a platform under formation.",
  },
  {
    n: "ii",
    title: "Single- & multi-family offices",
    body: "Principal families and their investment offices seeking direct, asset-backed private credit exposure with reporting and governance built for a fiduciary standard.",
  },
  {
    n: "iii",
    title: "Private banks, EAMs & DIFC advisers",
    body: "Wealth platforms and independent asset managers whose professional clients look for documented, senior-secured alternatives — engaged strictly on a by-introduction basis.",
  },
  {
    n: "iv",
    title: "Strategic & operating groups",
    body: "Regional industrial, healthcare and life-sciences groups exploring co-investment, capital solutions or a strategic relationship with the platform.",
  },
];

const STEPS = [
  {
    n: "i",
    title: "Introduction in the Gulf",
    body: "A first conversation with the Gulf Region Lead in Dubai — objectives, mandate, constraints and timing. No materials are circulated before qualification.",
  },
  {
    n: "ii",
    title: "Qualification & mandate fit",
    body: "Professional-investor status confirmed; the relationship is handed to Capital Formation, which reviews fit against the platform's mandates and vehicle architecture.",
  },
  {
    n: "iii",
    title: "Documented dialogue with the partnership",
    body: "Direct engagement with the founding partners and Head of Capital Formation. Diligence materials, governance documentation and Investment Committee standards are shared under NDA.",
  },
  {
    n: "iv",
    title: "Documentation & funding",
    body: "Terms, structure and documentation are concluded by the partnership and its counsel. Regional coverage remains available as a relationship point throughout the life of the commitment.",
  },
];

const FINDS = [
  {
    title: "Asset-backed, senior-secured discipline",
    body: "Industrial credit and bridge capital written to tangible collateral, contracted cash flow and structural seniority — not to a growth narrative.",
  },
  {
    title: "An institutional vehicle architecture",
    body: "Nine intended vehicles across funds, co-investment channels, business lines and separately managed accounts, on an institutional Cayman platform with independent administration, custody and audit.",
  },
  {
    title: "Structuring on a documented, case-by-case basis",
    body: "Currency, vehicle domicile, reporting cadence and structuring preferences — including requests for Shariah-compatible participation — are addressed with the partnership and its counsel, in writing.",
  },
  {
    title: "A regional relationship point",
    body: "A named contact in Dubai, in the region's time zone, who knows the partnership's mandates first-hand — and a life-sciences and healthcare lens on Gulf origination.",
  },
];

const BASIS = [
  "Kellermann Reid & Co. is not licensed or regulated by the Dubai Financial Services Authority, the Securities and Commodities Authority or any other authority in the United Arab Emirates or the GCC, and maintains no regulated office, branch or licensed activity in the region.",
  "Regional coverage is introductory. The Gulf Region Lead engages on documented, non-executive terms, does not hold partner economics and does not act as a placement agent, marketing distributor or authorised representative of the firm.",
  "Nothing on this page constitutes a financial promotion, an offer to sell or a solicitation of an offer to buy any security or interest in any vehicle. Materials are made available only to persons who qualify as professional or qualified investors under applicable rules, on a by-introduction basis.",
  "Investment discussions, diligence, documentation and funding are conducted by the partnership directly, through its documented Selective Capital Pathways framework and the Investment Committee.",
];

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
      {children}
    </span>
    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
  </div>
);

const Diamond = ({ size = 6, mt = 8 }) => (
  <span
    aria-hidden
    className="block flex-shrink-0"
    style={{
      width: size,
      height: size,
      marginTop: mt,
      background: "var(--kr-gold)",
      transform: "rotate(45deg)",
    }}
  />
);

const GulfInvestorsPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid="kr-gulf-investors-page"
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Gulf Region"
          label="For Gulf Investors · UAE & GCC · Dubai (DIFC) Coverage"
          title={
            <>
              A{" "}
              <span className="italic text-[var(--kr-gold)]">
                direct, documented
              </span>{" "}
              path for Gulf capital.
            </>
          }
          description="How limited partners in the United Arab Emirates and the wider GCC engage with Kellermann Reid & Co. — by introduction, through a named regional contact, and on the same documented terms as every institutional relationship the partnership holds."
          breadcrumb="Gulf Investors"
        />

        {/* Arabic summary */}
        <section
          data-testid="kr-gulf-arabic"
          className="py-16 lg:py-20 border-t border-[var(--kr-line-soft)]"
          style={{ background: "var(--kr-bg-2)" }}
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div
              dir="rtl"
              lang="ar"
              data-testid="kr-gulf-arabic-text"
              className="lg:col-span-9 lg:order-2 font-arabic text-right"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[var(--kr-gold)] text-[14px]">
                  {ARABIC.eyebrow}
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2
                data-testid="kr-gulf-arabic-title"
                className="text-[var(--kr-text)] text-[2rem] lg:text-[2.6rem] leading-[1.35] mb-8"
              >
                {ARABIC.title}
              </h2>
              <div className="border-r-2 border-[var(--kr-gold)] pr-8 space-y-5 max-w-[70ch]">
                {ARABIC.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={
                      i === ARABIC.paragraphs.length - 1
                        ? "text-[var(--kr-text-muted)] text-[15.5px] leading-[2]"
                        : "text-[var(--kr-text)] text-[18px] leading-[2]"
                    }
                  >
                    {typeof p === "string" ? (
                      p
                    ) : (
                      <>
                        {p.pre}
                        <span dir="ltr" className="font-serif-display text-[17px]">
                          {ARABIC.latinName}
                        </span>
                        {p.post}
                      </>
                    )}
                  </p>
                ))}
              </div>
              <a
                href={`mailto:${SVEN.email}`}
                className="inline-block mt-8 text-[var(--kr-gold)] text-[16px] hover:text-[var(--kr-text)] transition-colors"
              >
                {ARABIC.contact} —{" "}
                <span dir="ltr" className="font-serif-display">
                  {SVEN.email}
                </span>
              </a>
            </div>

            <div className="lg:col-span-3 lg:order-1">
              <Eyebrow>Arabic Summary</Eyebrow>
              <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.8] max-w-[34ch]">
                A brief summary for regional readers. The full page follows in
                English; formal correspondence is conducted in English.
              </p>
              <a
                href={`mailto:${SVEN.email}`}
                data-testid="kr-gulf-arabic-contact"
                className="kr-link font-mono-label text-[10px] inline-block mt-6"
              >
                Contact the Gulf Region Lead →
              </a>
            </div>
          </div>
        </section>

        {/* Letter from the Gulf Region Lead */}
        <section
          data-testid="kr-gulf-letter"
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <Eyebrow>A note from the Gulf Region Lead</Eyebrow>
                <div className="flex items-start gap-5">
                  <div className="relative w-24 h-28 flex-shrink-0 overflow-hidden border border-[var(--kr-line)] bg-[#0c1018]">
                    <img
                      src={SVEN.photo}
                      alt={SVEN.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{
                        filter:
                          "grayscale(35%) contrast(1.05) brightness(0.95) saturate(0.9)",
                        objectPosition: "center top",
                      }}
                    />
                    <span className="absolute top-1 left-1 w-3 h-3 border-l border-t border-[var(--kr-gold)]/70" />
                    <span className="absolute top-1 right-1 w-3 h-3 border-r border-t border-[var(--kr-gold)]/70" />
                    <span className="absolute bottom-1 left-1 w-3 h-3 border-l border-b border-[var(--kr-gold)]/70" />
                    <span className="absolute bottom-1 right-1 w-3 h-3 border-r border-b border-[var(--kr-gold)]/70" />
                  </div>
                  <div>
                    <div className="font-serif-display text-[var(--kr-text)] text-[1.45rem] leading-[1.1] mb-2">
                      {SVEN.name}
                    </div>
                    <div className="font-mono-label text-[9px] text-[var(--kr-gold)] mb-2">
                      {SVEN.role}
                    </div>
                    <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[12.5px] leading-[1.45]">
                      Senior Advisor · Life Sciences &amp; Pharma
                    </div>
                  </div>
                </div>
                <div className="kr-rule my-8" />
                <a
                  href={`mailto:${SVEN.email}`}
                  data-testid="kr-gulf-letter-email"
                  className="kr-link font-serif-display text-[0.95rem] text-[var(--kr-text)] break-all block mb-6"
                >
                  {SVEN.email}
                </a>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={`mailto:${SVEN.email}`}
                    data-testid="kr-gulf-letter-contact"
                    className="kr-btn-primary"
                    style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
                  >
                    Contact Sven
                    <span aria-hidden>→</span>
                  </a>
                  <Link
                    to="/regional-coverage#sven-massenhove"
                    data-testid="kr-gulf-letter-profile"
                    className="kr-btn-ghost"
                    style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
                  >
                    Full Profile →
                  </Link>
                </div>
              </div>
            </aside>

            <article className="lg:col-span-8 lg:pl-4">
              {LETTER.map((p, i) => (
                <p
                  key={i}
                  className={
                    i === 0
                      ? "font-serif-display text-[var(--kr-text)] text-[1.3rem] lg:text-[1.45rem] leading-[1.6] border-l-2 border-[var(--kr-gold)] pl-8 mb-10 max-w-[60ch]"
                      : "text-[var(--kr-text-muted)] text-[16px] leading-[1.95] mb-8 max-w-[66ch]"
                  }
                >
                  {p}
                </p>
              ))}
              <div className="mt-10 flex items-center gap-4">
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                <div>
                  <div className="font-serif-display italic text-[var(--kr-text)] text-[1.15rem]">
                    Sven Massenhove
                  </div>
                  <div className="font-mono-label text-[9.5px] text-[var(--kr-text-muted)] mt-1">
                    Gulf Region Lead · Dubai (DIFC)
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Who we engage */}
        <section
          data-testid="kr-gulf-who"
          className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <Eyebrow>Who We Engage</Eyebrow>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[26ch]">
                  Four kinds of Gulf capital,{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    one standard
                  </span>{" "}
                  of engagement.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pl-8">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[58ch]">
                  Regional coverage is deliberately narrow. The partnership
                  engages professional and qualified investors only, by
                  introduction, and prefers a small number of considered
                  relationships to a broad distribution.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {WHO.map((w) => (
                <article
                  key={w.n}
                  data-testid={`kr-gulf-who-${w.n}`}
                  className="p-8 lg:p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div
                    className="w-12 h-12 border border-[var(--kr-gold)]/60 flex items-center justify-center font-serif-display text-[1.05rem] italic text-[var(--kr-gold)] mb-6 uppercase"
                    aria-hidden
                  >
                    {w.n}
                  </div>
                  <h3 className="font-serif-display text-[1.2rem] text-[var(--kr-text)] mb-4 leading-[1.25] group-hover:text-[var(--kr-gold)] transition-colors">
                    {w.title}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.75]">
                    {w.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* How engagement works */}
        <section
          data-testid="kr-gulf-steps"
          className="py-20 lg:py-28 border-t border-[var(--kr-line)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <Eyebrow>How Engagement Works</Eyebrow>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[26ch]">
                  From a conversation in Dubai to a{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    documented
                  </span>{" "}
                  commitment.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pl-8">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[58ch]">
                  The Gulf pathway is the partnership&apos;s standard pathway
                  with a regional front door. Introductions are made in the
                  region; every subsequent step is taken with the partnership
                  directly.
                </p>
              </div>
            </div>
            <div className="border-t border-[var(--kr-line)]">
              {STEPS.map((s) => (
                <div
                  key={s.n}
                  data-testid={`kr-gulf-step-${s.n}`}
                  className="grid lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.02)] transition-colors duration-500"
                >
                  <div className="lg:col-span-1 font-serif-display italic text-[var(--kr-gold)] text-[1.4rem] uppercase">
                    {s.n}.
                  </div>
                  <div className="lg:col-span-4">
                    <h3 className="font-serif-display text-[var(--kr-text)] text-[1.35rem] leading-[1.25] group-hover:text-[var(--kr-gold)] transition-colors">
                      {s.title}
                    </h3>
                  </div>
                  <div className="lg:col-span-7">
                    <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.85] max-w-[62ch]">
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Gulf capital finds here */}
        <section
          data-testid="kr-gulf-finds"
          className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>What Gulf Capital Finds Here</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[22ch]">
                Underwriting culture,{" "}
                <span className="italic text-[var(--kr-gold)]">
                  not marketing
                </span>{" "}
                culture.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <ul className="space-y-6">
                {FINDS.map((f, i) => (
                  <li
                    key={f.title}
                    data-testid={`kr-gulf-find-${i}`}
                    className="flex items-start gap-5 border-b border-[var(--kr-line-soft)] pb-6"
                  >
                    <Diamond />
                    <div>
                      <div className="font-serif-display text-[var(--kr-text)] text-[1.2rem] leading-[1.3] mb-2">
                        {f.title}
                      </div>
                      <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.8] max-w-[62ch]">
                        {f.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Basis of engagement */}
        <section
          data-testid="kr-gulf-basis"
          className="py-20 lg:py-24 border-t border-[var(--kr-line)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Basis of Engagement</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[24ch]">
                Introductory in the region.{" "}
                <span className="italic text-[var(--kr-gold)]">
                  Concluded
                </span>{" "}
                by the partnership.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <ul className="space-y-4 mb-8">
                {BASIS.map((b, i) => (
                  <li
                    key={i}
                    data-testid={`kr-gulf-basis-${i}`}
                    className="flex items-start gap-4 text-[var(--kr-text)] text-[15px] leading-[1.85] border-b border-[var(--kr-line-soft)] pb-4"
                  >
                    <Diamond />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <Link
                  to="/capital-pathways"
                  data-testid="kr-gulf-pathways-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Selective Capital Pathways →
                </Link>
                <Link
                  to="/disclosures"
                  data-testid="kr-gulf-disclosures-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Disclosures →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-20 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.2] max-w-[34ch]">
                Begin with a conversation{" "}
                <span className="italic text-[var(--kr-gold)]">in Dubai</span>{" "}
                — or directly with the partnership.
              </h2>
              <p className="mt-5 text-[var(--kr-text-muted)] text-[15px] leading-[1.85] max-w-[60ch]">
                Gulf-based allocators may contact the Gulf Region Lead directly,
                or request access through the partnership&apos;s Investor
                Relations desk. Both routes lead to the same documented dialogue.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <a
                href={`mailto:${SVEN.email}`}
                data-testid="kr-gulf-cta-contact"
                className="kr-btn-primary"
              >
                Contact Sven
                <span aria-hidden>→</span>
              </a>
              <button
                type="button"
                onClick={openModal}
                data-testid="kr-gulf-cta-access"
                className="kr-btn-ghost"
              >
                Request Investor Access
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default GulfInvestorsPage;
